import { put, select, call } from 'redux-saga/effects';

import { makeRequest } from '@core/js/sagas/makeRequest';

import {
    COOKIE_NAME_URBN_PRIVACY_OPTOUT,
    COOKIE_NAME_HAS_DISMISSED_PRIVACY_NOTICE,
    COOKIE_PERSONALIZATION_CONTEXT
} from '@core/js/constants';
import Cookies from '@core/js/services/cookies';

import { reloadPage } from '@core/js/utils';
import { getLoggedInState } from '@core/js/utils/authUtils';
import { patchProfileGlobalPreferences, createProfilePreferences } from '@core/js/utils/apiRoutes';
import { error } from '@core/js/utils/logger';

import {
    dismissPrivacyNotice,
    optOutUser,
    setPolicyResidency
} from '@core/js/actions/privacyPreferencesActions';

function getOptedOutCookieName(state) {
    const privacyPolicyRegion = _.get(state, 'core.privacyRestriction.region', '').toLowerCase();
    const privacyPolicyCountry = _.get(state, 'core.privacyRestriction.country', '').toLowerCase();

    return `${COOKIE_NAME_URBN_PRIVACY_OPTOUT}_${privacyPolicyCountry}_${privacyPolicyRegion}`;
}

function hasOptOutCookie(state) {
    const optedOutCookieName = getOptedOutCookieName(state);

    return Boolean(Cookies.getCookieByName(optedOutCookieName));
}

function hasDismissedBanner(state) {
    const privacyPolicyRegion = _.get(state, 'core.privacyRestriction.region', '').toLowerCase();
    const hasDismissedBannerCookieName = `${COOKIE_NAME_HAS_DISMISSED_PRIVACY_NOTICE}_${privacyPolicyRegion}`;

    return Boolean(Cookies.getCookieByName(hasDismissedBannerCookieName));
}

function hasOptOutProfile(state) {
    const privacyPolicyRegion = _.get(state, 'core.privacyRestriction.region', '');
    const profileOptOutLocations = _.get(state, 'core.profile.privacyOptOutLocations', []);
    const profileHasOptedOutOfPolicy = _.some(profileOptOutLocations, ['regionCode', privacyPolicyRegion]);

    return profileHasOptedOutOfPolicy;
}

function isResident(state) {
    const privacyPolicyRegion = _.get(state, 'core.privacyRestriction.region', '');
    const profilePrivacyLocations = _.get(state, 'core.profile.privacyLocations', []);
    const userIsResident = _.some(profilePrivacyLocations, ['regionCode', privacyPolicyRegion]);

    return userIsResident;
}

export function* requestOptOutSaga(state) {
    const userIsLoggedIn = getLoggedInState();
    const userHasOptOutCookie = hasOptOutCookie(state);

    if (userHasOptOutCookie) {
        return;
    }

    if (userIsLoggedIn) {
        yield call(updateOptOutPreferencesSaga, state);
    }

    yield call(setOptOutCookieSaga);
}

export function* privacyPreferencesCookieSaga() {
    const state = yield select();
    const userHasOptOutCookie = hasOptOutCookie(state);
    const userHasDismissedBanner = hasDismissedBanner(state);

    if (userHasDismissedBanner) {
        yield put(dismissPrivacyNotice(true));
    }

    if (userHasOptOutCookie) {
        yield put(optOutUser());
    }
}

export function* setPrivacyPreferencesSaga() {
    const state = yield select();
    const userHasOptOutCookie = hasOptOutCookie(state);
    const userHasOptOutProfile = hasOptOutProfile(state);
    const isResidentSet = _.get(state, 'core.privacyRestriction.userIsResident', false);

    if (!isResidentSet && isResident(state)) {
        yield put(setPolicyResidency());
    }

    if (userHasOptOutCookie && !userHasOptOutProfile) {
        yield call(updateOptOutPreferencesSaga, state);
    } else if (!userHasOptOutCookie && userHasOptOutProfile) {
        yield call(setOptOutCookieSaga);
    }
}

export function* updateOptOutPreferencesSaga(state) {
    const privacyPolicyRegion = _.get(state, 'core.privacyRestriction.region', '');
    const privacyPolicyCountry = _.get(state, 'core.privacyRestriction.country', '');
    const optOutLocations = { 'regionCode': privacyPolicyRegion, 'countryCode': privacyPolicyCountry };

    try {
        const entity = [{
            op: 'add',
            path: '/global/privacyOptOutLocations',
            value: [optOutLocations]
        }];

        const route = patchProfileGlobalPreferences(state, entity);
        yield call(makeRequest, route);
    } catch (err) {

        if (err.status && err.status.code === 412) {
            const route = createProfilePreferences(state, { global: { 'privacyOptOutLocations': [optOutLocations] } });
            yield call(makeRequest, route);
        } else {
            error('Opt Out Patch Error', err);
        }
    }
}

export function* setOptOutCookieSaga() {
    const state = yield select();
    const optedOutCookieName = getOptedOutCookieName(state);

    Cookies.setCookie(
        optedOutCookieName,
        'true'
    );
    Cookies.deleteCookie(COOKIE_PERSONALIZATION_CONTEXT);

    reloadPage();
}
